import {useCallback, useState} from 'react';

import {InlineEditState} from '../components/InlineLabel';
import {labelText} from '../lib/label-text';
import {trimRichText, type RichText} from '../lib/rich-text';
import type {ImageCrop, LogoConfig, PhotoRequirement} from '../lib/types';

interface UsePreviewEditingParams {
  fillMode: boolean;
  requirements: PhotoRequirement[];
  onLabelChange?: (id: string, label: RichText) => void;
  onElementChange?: (updated: PhotoRequirement) => void;
}

/**
 * Inline label editing + photo/logo crop editing for the preview. Double-click
 * routes a logo-with-image into crop mode and everything else into inline label
 * editing; the returned `inlineEdit` object drives the editable label textareas.
 */
export function usePreviewEditing({
  fillMode,
  requirements,
  onLabelChange,
  onElementChange,
}: UsePreviewEditingParams) {
  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineEditValue, setInlineEditValue] = useState<RichText>({text: ''});

  const inlineEdit: InlineEditState = {
    id: inlineEditId,
    value: inlineEditValue,
    onChange: setInlineEditValue,
    onCommit: useCallback(() => {
      // Trimmed with its marks, so a leading space cut off does not shift every
      // style one character to the left.
      const trimmed = trimRichText(inlineEditValue);
      if (inlineEditId && trimmed.text) {
        onLabelChange?.(inlineEditId, trimmed);
      }
      setInlineEditId(null);
    }, [inlineEditId, inlineEditValue, onLabelChange]),
    onCancel: useCallback(() => setInlineEditId(null), []),
  };

  const startInlineEdit = useCallback(
    (item: PhotoRequirement) => {
      if (fillMode) return;
      setInlineEditId(item.id);
      setInlineEditValue(labelText(item));
    },
    [fillMode],
  );

  // ── Photo (logo) crop editing ───────────────────────────────────────────────
  const [cropEditId, setCropEditId] = useState<string | null>(null);

  // Double-click routing: a "Photo" (logo) with an image opens crop mode;
  // everything else inline-edits its label.
  const startEditOrCrop = useCallback(
    (item: PhotoRequirement) => {
      if (fillMode) return;
      if (item.type === 'logo' && (item.config as LogoConfig).logo_url) {
        setInlineEditId(null);
        setCropEditId(item.id);
      } else {
        startInlineEdit(item);
      }
    },
    [fillMode, startInlineEdit],
  );

  const handleCropApply = useCallback(
    (crop: ImageCrop) => {
      if (!cropEditId) return;
      const it = requirements.find((r) => r.id === cropEditId);
      if (it) onElementChange?.({...it, config: {...it.config, crop}} as PhotoRequirement);
      setCropEditId(null);
    },
    [cropEditId, requirements, onElementChange],
  );

  const handleCropCancel = useCallback(() => setCropEditId(null), []);

  return {
    inlineEdit,
    inlineEditId,
    startInlineEdit,
    cropEditId,
    startEditOrCrop,
    handleCropApply,
    handleCropCancel,
  };
}
